import {
  autoUpdate,
  flip,
  FloatingFocusManager,
  FloatingPortal,
  offset,
  size,
  useDismiss,
  useFloating,
  useInteractions,
  useListNavigation,
  useRole
} from "@floating-ui/react"
import { useQuery } from "@tanstack/react-query"
import clsx from "clsx"
import { motion } from "framer-motion"
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"
import type { FieldValues } from "react-hook-form"
import { Controller } from "react-hook-form"
import { Icon } from "@/shared/components"
import type { ISelectRenderOptions } from "@/shared/components/common/select/types.ts"
import { tCommon, tError } from "@/shared/config/i18n"
import { Api } from "@/shared/lib"
import type { IFormControl, IOption, Nullable } from "@/shared/types/index.ts"
import { cvToConstant, findOption, TypeCheck } from "@/shared/utils"
import { Animations } from "@/shared/utils/animations.ts"
import { Item } from "./Item.tsx"
import "./style.scss"
import { useTranslation } from "react-i18next"
import { Loader, Search, SelectArrow } from "@/shared/components/ui/icons"

interface ISelectProps<T extends string | number, M extends object> {
  options: IOption<T, M>[]
  value?: T | IOption<T, M>
  onSelect?: (value: Nullable<IOption<T, M>>) => void
  treeSelect?: boolean
  placeholder?: string
  label?: string
  renderOption?: ISelectRenderOptions<T, M>
  leftIcon?: React.ReactNode
  rightIcon?: React.ReactNode
  className?: string
  labelClassName?: string
  disabled?: boolean
  loading?: boolean
  onOpen?: () => void
  noOptionsText?: string
  error?: string
  required?: boolean
  ref?: React.Ref<HTMLInputElement>
  firstSelected?: boolean
  isFetchingNextPage?: boolean
  debounceDelay?: number
  onScrollEnd?: () => void
  onSearch?: (value: string) => void
  isSearchable?: boolean
}

export default function Select<T extends string | number, M extends object>({
  className,
  labelClassName,
  disabled,
  options,
  value,
  treeSelect = false,
  onSelect,
  error,
  required,
  firstSelected,
  debounceDelay,
  onSearch,
  isSearchable = false,
  isFetchingNextPage,
  onScrollEnd,
  ...props
}: ISelectProps<T, M>) {
  // Refs
  const listRef = useRef<Array<HTMLElement | null>>([])
  const debounceTimer = useRef<NodeJS.Timeout | null>(null)
  const listWrapperRef = useRef<HTMLDivElement | null>(null)

  // States
  const [open, setOpen] = useState(false)
  const [activeIndex, setActiveIndex] = useState<number | null>(null)
  const [selected, setSelected] = useState<IOption<T, M> | null>(null)
  const [filterValue, setFilterValue] = useState<string | null>(null)
  const [inputValue, setInputValue] = useState("")

  const { refs, floatingStyles, context } = useFloating<HTMLInputElement>({
    whileElementsMounted: autoUpdate,
    open,
    onOpenChange: setOpen,
    middleware: [
      flip({ padding: 10 }),
      offset(8),
      size({
        apply({ rects, elements }) {
          Object.assign(elements.floating.style, {
            width: `${rects.reference.width}px`,
            maxHeight: "400px"
          })
        },
        padding: 10
      })
    ]
  })
  const { t } = useTranslation("errors")
  const role = useRole(context, { role: "listbox" })
  const dismiss = useDismiss(context)
  const listNav = useListNavigation(context, {
    listRef,
    activeIndex,
    onNavigate: setActiveIndex,
    virtual: true,
    loop: true
  })

  const filteredOptions = useMemo(() => {
    if (inputValue.length === 0 || debounceDelay) return options
    return options.filter(option => option.label.toLowerCase().includes(filterValue?.toLowerCase() ?? ""))
  }, [filterValue, options])

  const { getReferenceProps, getFloatingProps, getItemProps } = useInteractions([role, dismiss, listNav])

  const handleSelect = (option: IOption<T, M>) => {
    setOpen(false)
    setSelected(option)
    setInputValue(option.label)
    setFilterValue(null)
    onSelect?.(option)
    if (debounceDelay) {
      onSearch?.("")
    }
  }

  const putInputValue = (val?: IOption<T, M>) => {
    if (debounceDelay) {
      setInputValue(filterValue !== null ? filterValue : (val?.label ?? ""))
    } else {
      setInputValue(val?.label ?? "")
    }
  }

  useEffect(() => {
    if (isFetchingNextPage && listWrapperRef.current) {
      const el = listWrapperRef.current
      el.scrollTo({
        top: el.scrollHeight,
        behavior: "smooth"
      })
    }
  }, [isFetchingNextPage])

  useEffect(() => {
    if (TypeCheck.isString(value) || TypeCheck.isNumber(value)) {
      let option: IOption<T, M> | undefined
      if (treeSelect) {
        option = findOption<T, M>(options, value)
      } else {
        option = options.find(option => option.value === value)
      }
      setSelected(option ?? null)
      putInputValue(option)
    } else {
      putInputValue(value)
      setSelected(value ?? null)
    }
  }, [options, value])

  useEffect(() => {
    if (firstSelected && options.length > 0) {
      setSelected(options[0])
      setInputValue(options[0].label)
      setFilterValue("")
      onSelect?.(options[0])
    }
  }, [options])

  const handleClear = () => {
    setSelected(null)
    setOpen(true)
    onSelect?.(null)
    setInputValue("")
    if (debounceDelay) onSearch?.("")

    // Focus the input again
    requestAnimationFrame(() => {
      const input = refs.reference.current as HTMLInputElement | null
      input?.focus()
    })
  }

  const handleScroll = useCallback(
    (e: React.UIEvent<HTMLDivElement>) => {
      let isFetching = false
      const target = e.target as HTMLDivElement
      const atBottom = target.scrollHeight - (target.scrollTop + target.clientHeight) < 1

      if (atBottom && !isFetching) {
        isFetching = true
        onScrollEnd?.()
        setTimeout(() => {
          isFetching = false
        }, 1000)
      }
    },
    [onScrollEnd]
  )

  return (
    <>
      <div className={clsx("custom-select", className)}>
        {props.label != null && (
          <div className={clsx("text-label", error != null && "text-label__error")}>
            {props.label} {(required || error != null) && <strong className="text-[10px] text-red-500">＊</strong>}
          </div>
        )}
        <label
          className={clsx(
            labelClassName,
            props.label != null && "mt-1",
            "custom-select-wrapper",
            disabled && "custom-select-wrapper--disabled",
            props.loading ? "bg-zinc-50" : ""
          )}
          ref={refs.setReference}
          aria-autocomplete="list"
          onMouseDown={() => {
            const wasOpen = !open
            if (wasOpen && props.onOpen != null) props.onOpen()
            setOpen(wasOpen)
          }}
        >
          {!isSearchable ? (
            <input
              disabled={disabled || props.loading}
              type="text"
              spellCheck="false"
              className={props.loading ? "hover:cursor-progress" : ""}
              value={inputValue}
              onChange={e => {
                setInputValue(e.target.value)
                if (debounceDelay) {
                  if (debounceTimer.current) clearTimeout(debounceTimer.current)
                  debounceTimer.current = setTimeout(() => {
                    onSearch?.(e.target.value ?? "")
                    setFilterValue(e.target.value)
                  }, debounceDelay)
                }
              }}
              onFocus={() => {
                setOpen(true)
              }}
              onBlur={() => {
                setTimeout(() => {
                  setOpen(false)
                }, 100)
              }}
              onInput={e => {
                const value = e.currentTarget.value
                setFilterValue(value)

                if (debounceDelay) {
                  if (debounceTimer.current) clearTimeout(debounceTimer.current)
                  debounceTimer.current = setTimeout(() => {
                    onSearch?.(value ?? "")
                  }, debounceDelay)
                }
              }}
              placeholder={props.placeholder}
              onKeyDown={event => {
                if (event.key === "Enter") {
                  event.preventDefault()
                  if (activeIndex != null && options[activeIndex]) {
                    const option = options[activeIndex]
                    setInputValue(option.label)
                    setSelected(option)
                    setOpen(false)
                  }
                }
              }}
            />
          ) : (
            <div className="flex w-full min-w-[120px] items-center justify-between py-2">
              <span className={clsx("truncate", !selected ? "text-gray-400" : "")}>{selected ? selected.label : props.placeholder}</span>
            </div>
          )}
          <div className="flex w-12 justify-end gap-2">
            {selected != null && !disabled && (
              <Icon
                tabIndex={0}
                className="cursor-pointer rounded-sm text-DarkGray outline-none focus:ring-1 focus:ring-blue-600"
                onMouseDown={e => e.stopPropagation()}
                onKeyDown={event => {
                  if (event.key === "Enter") handleClear()
                }}
                onClick={handleClear}
                name="Close"
              />
            )}
            {props.loading && !isFetchingNextPage ? (
              <Loader className="animate-spin *:fill-zinc-400" style={{ animationDuration: "500ms" }} />
            ) : (
              <SelectArrow className="pointer-events-none text-DarkGray" onClick={e => e.stopPropagation()} />
            )}
          </div>
        </label>
        {error != null && <span className="text-label__error">{t(error)}</span>}
      </div>
      <FloatingPortal>
        {open && !disabled && (
          <FloatingFocusManager context={context} initialFocus={-1} visuallyHiddenDismiss>
            <div ref={refs.setFloating} style={floatingStyles} className="custom-select__list-wrapper" {...getFloatingProps()}>
              <motion.div
                className="custom-select__list"
                ref={listWrapperRef}
                transition={{ duration: 0.15 }}
                {...Animations("scaleOut")}
                onScroll={handleScroll}
              >
                {filteredOptions?.length === 0 ? (
                  <div className="custom-select__list-item">
                    <span className="text-slate-400">{props.noOptionsText ?? "No options"}</span>
                  </div>
                ) : (
                  filteredOptions.map((option, index) =>
                    props.renderOption != null && TypeCheck.isFunction(props.renderOption) ? (
                      props.renderOption(
                        {
                          ...getItemProps(),
                          ref(node) {
                            listRef.current[index] = node
                          },
                          className: clsx(
                            "custom-select__list-item",
                            activeIndex === index && "custom-select__list-item--active",
                            selected?.value === option.value && "custom-select__list-item--selected"
                          ),
                          onClick: () => handleSelect(option),
                          onMouseDown() {
                            requestAnimationFrame(() => {
                              ;(refs.reference.current as HTMLInputElement).focus()
                            })
                          }
                        },
                        option,
                        {
                          active: activeIndex === index,
                          selected: selected?.value === option.value,
                          index
                        }
                      )
                    ) : (
                      <>
                        {index === 0 && isSearchable && (
                          <div className="border-gray-100 border-b p-2">
                            <div className="relative">
                              <input
                                title="Search"
                                type="text"
                                value={inputValue}
                                onChange={e => {
                                  setInputValue(e.target.value)
                                  if (debounceDelay) {
                                    if (debounceTimer.current) clearTimeout(debounceTimer.current)
                                    debounceTimer.current = setTimeout(() => {
                                      onSearch?.(e.target.value ?? "")
                                      setFilterValue(e.target.value)
                                    }, debounceDelay)
                                  }
                                }}
                                onKeyDown={event => {
                                  if (event.key === "Enter" && activeIndex != null && options[activeIndex]) {
                                    const option = options[activeIndex]
                                    setInputValue(option.label)
                                    setSelected(option)
                                    setOpen(false)
                                  }
                                }}
                                className="w-full rounded-lg border border-gray-200 py-1.5 pr-3 pl-8 text-sm focus:border-blue-500 focus:outline-none"
                              />
                              <Search width={14} height={14} className="absolute top-2.5 left-2.5 text-gray-400" />
                            </div>
                          </div>
                        )}
                        <Item
                          key={index}
                          {...getItemProps()}
                          ref={node => {
                            listRef.current[index] = node
                          }}
                          onClick={() => handleSelect(option)}
                          active={activeIndex === index}
                          selected={selected?.value === option.value}
                          onMouseDown={e => {
                            e.preventDefault()
                            requestAnimationFrame(() => {
                              ;(refs.reference.current as HTMLInputElement).focus()
                            })
                          }}
                        >
                          {option.label}
                        </Item>
                      </>
                    )
                  )
                )}
                {isFetchingNextPage ? (
                  <div style={{ animationDuration: "700ms" }} className="custom-select__list-item flex animate-pulse justify-center bg-zinc-50">
                    <Loader className="animate-spin" style={{ animationDuration: "300ms" }} />
                  </div>
                ) : null}
              </motion.div>
            </div>
          </FloatingFocusManager>
        )}
      </FloatingPortal>
    </>
  )
}

const FormSelect = <T extends FieldValues, OptionType extends string | number, M extends object>({
  control,
  name,
  required,
  rules,
  ...props
}: IFormControl<T> & ISelectProps<OptionType, M>) => {
  return (
    <Controller
      {...{
        control,
        name,
        rules: { required: { value: required ?? false, message: tError("required_field") }, ...rules }
      }}
      render={({ field: { ref, ...field }, fieldState: { error } }) => (
        <Select
          {...{ ...field, ...props }}
          error={error?.message}
          onSelect={option => {
            field.onChange(option)
            props.onSelect?.(option)
          }}
          required={required}
        />
      )}
    />
  )
}

export interface IFormReferenceSelectProps<T extends FieldValues, OptionType extends string | number, M extends object>
  extends IFormControl<T>,
    Omit<ISelectProps<OptionType, M>, "options"> {
  url: string
  enabled?: boolean
  params?: {
    label?: string
    value?: string
    meta?: string[]
  }
  search?: boolean
  filters?: object
}

const FormReferenceSelect = <T extends FieldValues, OptionType extends string | number, M extends object>({
  control,
  name,
  required,
  rules,
  url,
  enabled = true,
  params,
  search = false,
  filters,
  ...props
}: IFormReferenceSelectProps<T, OptionType, M>) => {
  const [filter, setFilter] = useState<string>("")

  const { data } = useQuery({
    queryKey: [`REFERENCE_${cvToConstant(url)}`, filter, filters],
    queryFn: Api.getReference(url, { ...(filter ? { search: filter } : {}), ...filters }),
    enabled
  })

  const options: IOption<OptionType, M>[] = React.useMemo(() => {
    const array = Array.isArray(data?.data) ? data?.data : Array.isArray(data?.data?.results) ? data?.data?.results : []
    if (array.length === 0) return []
    return (
      array.map(option => ({
        label: String(option?.[params?.label ?? "name"] || "-"),
        value: option?.[params?.value ?? "id"],
        meta: params?.meta?.reduce(
          (acc, mapKey) => {
            acc[mapKey] = option?.[mapKey]
            return acc
          },
          {} as Record<string, unknown>
        ) as M
      })) ?? []
    )
  }, [data, params, url])

  return (
    <Controller
      {...{
        control,
        name,
        rules: { required: { value: required ?? false, message: tError("required_field") }, ...rules }
      }}
      render={({ field: { ref, ...field }, fieldState: { error } }) => (
        <Select
          {...{ ...field, ...props }}
          error={error?.message}
          onSelect={option => {
            field.onChange(option?.value ?? null)
            props.onSelect?.(option)
          }}
          {...(search && {
            onSearch: val => {
              setFilter(val)
            }
          })}
          options={options}
          required={required}
          debounceDelay={search ? 500 : undefined}
        />
      )}
    />
  )
}

Select.Form = FormSelect
Select.Reference = FormReferenceSelect
